/**
 * Attach an external URL, with auto-filled metadata — §2.8 #90.
 *
 * The 600ms debounced preview is the interesting part, and specifically what it
 * refuses to do: it **never overwrites a field the user has touched**.
 * `titleTouched` / `descTouched` exist for that alone. Without them, a user who
 * types a title and then edits the URL watches their title get replaced by
 * whatever the scraper found.
 *
 * The favicon is fetched from Google's service, which answers 200 with a
 * placeholder for unknown domains — so "did it load" cannot be read off the
 * request. A hidden probe `<img>` resolves it and the visible one only mounts
 * after `onLoad`, which is what avoids a flash of a broken-image icon. Offline
 * (a story, say) the probe simply errors and the neutral globe stays.
 */
import { useEffect, useState } from 'react';
import { Button, Input } from '@ai-tutor/ui';
import { Check, Globe, Loader2, Plus } from 'lucide-react';
import { notify } from '@/lib/notify';
import {
  useAddExtrasAttachment,
  usePreviewLink,
} from '@/hooks/lesson/useOnDemandContent';
import { useAutoFocus } from '@/hooks/ui/useAutoFocus';
import { parseHostname } from '@/lib/sources/url';

export interface LinkFormProps {
  lessonId: string;
  /** Fired after a successful add - modal stays open, form is cleared. */
  onAdded: (title: string) => void;
}

export function LinkForm({ lessonId, onAdded }: LinkFormProps) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [descTouched, setDescTouched] = useState(false);
  // Tracks whether the favicon image actually loaded - Google's favicon
  // service returns a 404 stub for unknown domains, but the request
  // still resolves "successfully", so we use onError to fall back to
  // a neutral globe icon.
  const [faviconLoaded, setFaviconLoaded] = useState(false);

  const previewLink = usePreviewLink();
  const addAttachment = useAddExtrasAttachment(lessonId);

  // Returns the ref, so `handleReset` below can still re-focus imperatively.
  const urlInputRef = useAutoFocus<HTMLInputElement>();

  const hostname = parseHostname(url);
  const isUrlValid = hostname !== null;

  // Reset favicon-loaded state whenever the hostname changes - a fresh
  // <img> with a different src will fire its own load/error events.
  useEffect(() => {
    setFaviconLoaded(false);
  }, [hostname]);

  // Auto-fetch preview metadata 600ms after the user stops typing - but
  // never overwrite fields the user has already edited (titleTouched /
  // descTouched). Keeps the form responsive without trampling input.
  useEffect(() => {
    if (!isUrlValid) return;
    const t = setTimeout(() => {
      previewLink.mutate(url.trim(), {
        onSuccess: data => {
          if (!titleTouched && data.title) setTitle(data.title);
          if (!descTouched && data.description)
            setDescription(data.description);
        },
      });
    }, 600);
    return () => clearTimeout(t);
    // `previewLink` is a stable mutation handle - re-running this effect
    // when its identity changes would re-fire the fetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, titleTouched, descTouched, isUrlValid]);

  const previewData = previewLink.data;
  const showPreviewCard =
    isUrlValid &&
    !previewLink.isPending &&
    previewData &&
    (previewData.title || previewData.description);

  const isValid = isUrlValid && title.trim().length > 0;

  const resetForm = () => {
    setUrl('');
    setTitle('');
    setDescription('');
    setTitleTouched(false);
    setDescTouched(false);
    previewLink.reset();
    urlInputRef.current?.focus();
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    const submittedTitle = title.trim();
    try {
      await addAttachment.mutateAsync({
        kind: 'link',
        url: url.trim(),
        title: submittedTitle,
        description: description.trim() || null,
      });
      onAdded(submittedTitle);
      resetForm();
    } catch (err: any) {
      notify.error(err, "Couldn't attach link");
    }
  };

  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 space-y-4'>
        <div>
          <label
            htmlFor='extras-link-url'
            className='mb-1.5 block text-sm font-medium text-foreground'
          >
            URL
          </label>
          {/* URL field with leading favicon and trailing validation
              indicator. Layout: relative wrapper, the input has lots of
              left/right padding to leave room for the absolutely-
              positioned adornments. */}
          <div className='relative'>
            <div className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2'>
              {isUrlValid && hostname && faviconLoaded ? (
                <img
                  src={`https://www.google.com/s2/favicons?domain=${hostname}&sz=32`}
                  alt=''
                  className='h-4 w-4 rounded-sm'
                  onLoad={() => setFaviconLoaded(true)}
                  onError={() => setFaviconLoaded(false)}
                />
              ) : (
                <Globe className='h-4 w-4 text-muted-foreground' />
              )}
              {/* Hidden probe so onLoad fires once the favicon resolves -
                  the visible <img> above only renders after that. Avoids
                  a flash of a broken-image icon. */}
              {isUrlValid && hostname && !faviconLoaded && (
                <img
                  src={`https://www.google.com/s2/favicons?domain=${hostname}&sz=32`}
                  alt=''
                  className='hidden'
                  onLoad={() => setFaviconLoaded(true)}
                  onError={() => setFaviconLoaded(false)}
                />
              )}
            </div>
            <Input
              id='extras-link-url'
              ref={urlInputRef}
              type='url'
              placeholder='https://example.com/article'
              value={url}
              onChange={e => setUrl(e.target.value)}
              className='bg-transparent pl-10 pr-10 focus-visible:ring-offset-0'
            />
            <div className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'>
              {previewLink.isPending ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
              ) : isUrlValid ? (
                <Check className='h-3.5 w-3.5 text-green-500' />
              ) : null}
            </div>
          </div>
        </div>

        {/* Preview card - shows once the backend has resolved the URL.
            Provides "yes, we found this" confirmation without forcing
            the user to read the prefilled text fields. */}
        {showPreviewCard && (
          <div className='flex animate-fade-in items-start gap-3 rounded-md border border-border/60 p-3'>
            {hostname && faviconLoaded ? (
              <img
                src={`https://www.google.com/s2/favicons?domain=${hostname}&sz=32`}
                alt=''
                className='mt-0.5 h-4 w-4 flex-shrink-0 rounded-sm'
              />
            ) : (
              <Globe className='mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground' />
            )}
            <div className='min-w-0 flex-1'>
              {hostname && (
                <div className='truncate text-xs text-muted-foreground'>
                  {hostname}
                </div>
              )}
              {previewData?.title && (
                <div className='truncate text-sm font-medium text-foreground'>
                  {previewData.title}
                </div>
              )}
              {previewData?.description && (
                <div className='line-clamp-2 text-xs text-muted-foreground'>
                  {previewData.description}
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label
            htmlFor='extras-link-title'
            className='mb-1.5 block text-sm font-medium text-foreground'
          >
            Title
          </label>
          <Input
            id='extras-link-title'
            placeholder='Resource name'
            value={title}
            onChange={e => {
              setTitle(e.target.value);
              setTitleTouched(true);
            }}
            className='bg-transparent focus-visible:ring-offset-0'
          />
        </div>

        <div>
          <label
            htmlFor='extras-link-description'
            className='mb-1.5 block text-sm font-medium text-foreground'
          >
            Description (optional)
          </label>
          <textarea
            id='extras-link-description'
            rows={2}
            placeholder='Why is this useful?'
            value={description}
            onChange={e => {
              setDescription(e.target.value);
              setDescTouched(true);
            }}
            className='w-full resize-none rounded-md border border-border bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:border-brand-400 focus:outline-none dark:focus:border-primary'
          />
        </div>
      </div>

      {/* No divider and no Cancel: the modal's close button, Escape and the
          backdrop all dismiss it already, and the Lesson tab (which attaches
          on click) has no footer at all, so the two tabs now end the same. */}
      {/* Full width and 44px on a phone, where the form is a sheet. */}
      <div className='mt-5 sm:flex sm:items-center sm:justify-end'>
        <Button
          size='sm'
          disabled={!isValid || addAttachment.isPending}
          onClick={handleSubmit}
          className='h-11 w-full sm:h-8 sm:w-auto'
        >
          {addAttachment.isPending ? (
            <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
          ) : (
            <Plus className='mr-1 h-3.5 w-3.5' />
          )}
          Add link
        </Button>
      </div>
    </div>
  );
}
